import { Progress, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { ShouldRender } from '../../shared/basic/ShouldRender';
import EmptyState from '../../shared/EmptyState';
import { formatDuration } from '../../shared/formateTime';
import { getLoginToken } from '../../shared/getToken';
import { notify } from '../../shared/notify';
import { CourseDummy } from '../../../../assets';
import { tokenUpdate } from '../../../atoms/authModalControl';
import { getMyCourses, syncCourses } from '../../../services/Courses';

const Courses = () => {
  const router = useRouter();
  const [myCourses, setMyCourses] = useState<
    {
      id: string;
      course_image_url: string;
      name: string;
      completionPercentage: number;
      slug: string;
      duration: number;
    }[]
  >([]);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [myCourseLoading, setMyCourseLoading] = useState(false);

  const screens = useBreakpoint();

  const handleGetMyCourse = async () => {
    const token = await getLoginToken();
    if (token) {
      try {
        await syncCourses();
        setMyCourseLoading(true);
        const response = await getMyCourses();
        const { data } = response;

        const formattedCourses = data?.map((course: any) => ({
          id: course?.course?.id,
          course_image_url: course?.course?.course_image_url,
          name: course?.course?.name,
          completionPercentage: course?.completion,
          slug: course?.course?.slug,
          duration: course?.course?.totalVideoDuration,
        }));
        setMyCourses(formattedCourses);
      } catch (error: any) {
        notify(error, 'error');
      } finally {
        setMyCourseLoading(false);
      }
    }
  };

  useEffect(() => {
    handleGetMyCourse();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  return (
    <div className='relative overflow-hidden '>
      <div>
        <div className='flex justify-center xs:justify-start items-center'>
          <div className='grid grid-cols-1 gap-y-3 xs:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:flex   xl:flex-wrap'>
            {!myCourseLoading
              ? myCourses?.map((item, index) => {
                  return (
                    <span
                      key={`${item?.id}-${index}`}
                      onClick={() => {
                        const slug = item?.slug;
                        if (slug) {
                          router.push(`/course/${slug}`);
                        }
                      }}
                      className='flex flex-col gap-y-1 p-1 md:p-1.5  cursor-pointer'
                    >
                      <span className='w-full  h-[100px]  lg:max-w-[316px] lg:min-w-[280px] xl:min-w-[280px] lg:max-h-[176px] xl:min-h-[150px]'>
                        <Image
                          src={item.course_image_url || CourseDummy}
                          alt='courseImage'
                          width={320}
                          height={170}
                          className='rounded-md transition-transform transform hover:scale-[1.03] duration-500 w-[200px] h-[100px]  lg:max-w-[316px] xl:min-w-[280px] lg:max-h-[176px] xl:min-h-[150px]'
                        />
                      </span>

                      <div className='flex flex-col '>
                        <ShouldRender
                          check={
                            item?.completionPercentage &&
                            item?.completionPercentage !== 0
                          }
                        >
                          <span className='flex gap-x-2 text-sm'>
                            <Progress
                              percent={item?.completionPercentage}
                              showInfo={false}
                              className='customProcessBar'
                            />{' '}
                            {item?.completionPercentage}%
                          </span>
                        </ShouldRender>
                        <ShouldRender check={item.duration}>
                          <span className='text-textGrayColor text-xs md:text-sm '>
                            {formatDuration(item?.duration as number)}
                          </span>
                        </ShouldRender>
                        <span className='text-white text-sm xl:text-base font-medium max-w-[150px] md:w-[200px] lg:max-w-[316px] lg:min-w-[300px] '>
                          {item.name}
                        </span>
                      </div>
                    </span>
                  );
                })
              : Array.from({ length: 10 }).map((_, index) => (
                  <span
                    key={index}
                    className='group cursor-pointer p-1 md:p-1.5  bg-transparent no-select '
                  >
                    <Skeleton.Button
                      style={{
                        width: screens.lg ? '300px' : '200px',
                        height: screens.xl ? '250px' : '150px',
                      }}
                      active={true}
                      className='rounded-sm md:rounded-md mt-3 transition-transform transform hover:scale-[1.03] duration-500 w-[150px] h-[100px]  lg:max-w-[316px] lg:min-w-[300px] lg:max-h-[176px] lg:min-h-[150px]'
                    />
                  </span>
                ))}
          </div>
          <ShouldRender check={!myCourses?.length && !myCourseLoading}>
            <div className='flex w-full justify-center items-center '>
              <EmptyState
                heading='No Courses right now'
                link='/courses/'
                desc='Your purchased courses will be visible here'
              />
            </div>
          </ShouldRender>
        </div>
      </div>
    </div>
  );
};

export default Courses;
